import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Dialog } from "./Dialog";
import { Button } from "./Button";
import "./ConfirmDialog.scss";

export type ConfirmDialogProps = {
  open: boolean;
  /** Closed: `confirmed` is true after a successful confirm, false on Cancel / ✕ / Escape. */
  onClose: (confirmed: boolean) => void;
  /** The action. While its promise runs the dialog is busy; if it throws, the dialog stays open. */
  onConfirm: () => unknown | Promise<unknown>;
  title: React.ReactNode;
  /** The question, e.g. "This action cannot be undone. …" */
  message?: React.ReactNode;
  /** A bold line above the message, e.g. "Are you sure?" */
  heading?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  /** danger = red button and a warning icon (deletes); primary = orange */
  tone?: "danger" | "primary";
  /** Extra content under the message (a checkbox, a reason field…) */
  children?: React.ReactNode;
  /** Disable the confirm button (e.g. until a reason is typed) */
  confirmDisabled?: boolean;
  /** Controlled busy state, when the caller tracks it itself */
  loading?: boolean;
};

/** "Are you sure?" — the one confirm / delete dialog. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  heading,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  children,
  confirmDisabled = false,
  loading,
}: ConfirmDialogProps): React.JSX.Element {
  const [running, setRunning] = useState(false);
  const busy = loading ?? running;

  const confirm = async () => {
    setRunning(true);
    try {
      await onConfirm();
      setRunning(false);
      onClose(true);
    } catch {
      // The caller reports the error; the dialog stays open so the admin can retry or cancel.
      setRunning(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      title={title}
      size="sm"
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={() => onClose(false)} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={tone === "danger" ? "danger" : "primary"} onClick={confirm} loading={busy} disabled={confirmDisabled}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className={`ui-confirm ui-confirm--${tone}`}>
        {tone === "danger" ? (
          <span className="ui-confirm__icon" aria-hidden="true">
            <AlertTriangle size={20} />
          </span>
        ) : null}
        <div className="ui-confirm__text">
          {heading ? <p className="ui-confirm__heading">{heading}</p> : null}
          {message ? <div className="ui-confirm__message">{message}</div> : null}
          {children}
        </div>
      </div>
    </Dialog>
  );
}

export default ConfirmDialog;
